import React, { FC } from "react";
import { defineMessages, useIntl } from "react-intl";
import {
  DrawRequestContext,
  ErrorContext,
  HeapAnalyticDataContext,
  UserContext,
} from "@app/provider";
import { ScheduleBloodDrawScreens } from "../scheduleBloodDraw";
import { AuthTitle } from "@app/components";
import { HEAP_EVENTS, HeapEventLocation } from "@app/provider/types";
import { TestCardStatus } from "@app/provider/testData/types";
import { useSetUserLanguage, useTokenizedLinks } from "@app/hooks";
import { useDialog } from "@natera/platform/lib/hooks";
import AcknowledgementsDialog, {
  ACKNOWLEDGMENT_FLOW,
} from "@app/components/acknowledgmentsDialog/acknowledgmentsDialog";
import { DOB_VALIDATION_ERROR } from "@app/components/forms/dobVerificationForm/dobVerificationForm";
import { DateOfBirthVerificationScreen } from "@app/pages/private/dateOfBirthVerification";

const messages = defineMessages({
  welcomeBloodDrawDobTitle: {
    id: "welcomeBloodDrawDobTitle",
    defaultMessage: "Welcome to Natera’s Patient Portal",
  },
  welcomeBloodDrawDobSubTitle: {
    id: "welcomeBloodDrawDobSubTitle",
    defaultMessage: "Please enter your date of birth to proceed.",
  },
});

interface WelcomeDobVerificationScreenProps {
  token: string;
  setBloodDrawScreen: React.Dispatch<
    React.SetStateAction<ScheduleBloodDrawScreens>
  >;
}

type HandleValidateDateOfBirth = ({
  dateOfBirth,
  phone,
}: {
  dateOfBirth?: string;
  phone?: string;
}) => Promise<boolean>;
type HandleValidatePhone = (phoneNumber?: string) => Promise<boolean>;

const ATTEMPTS = 3;

enum GUEST_CHOICE {
  login = "login",
  guest_flow = "guest_flow",
}

const WelcomeDobScreen: FC<WelcomeDobVerificationScreenProps> = ({
  token,
  setBloodDrawScreen,
}) => {
  const intl = useIntl();
  const { setError } = React.useContext(ErrorContext);
  const { getTokenLanguage } = useTokenizedLinks();
  const setUserLanguage = useSetUserLanguage();

  const acknowdledgementsDialog = useDialog(AcknowledgementsDialog);

  const { drawRequestDataForHeapEventData } = React.useContext(
    HeapAnalyticDataContext
  );

  const { verifySampleDrawWithoutLogging, isLoading, checkPhoneIsRequired } =
    React.useContext(DrawRequestContext);

  const { validatePhoneNumber } = React.useContext(UserContext);

  const [isPhoneRequired, setPhoneRequired] = React.useState<boolean>(false);

  const attemptsLeftRef = React.useRef<number>(ATTEMPTS);

  const handleCheckPhoneIsRequired = async (token: string) => {
    const isPhoneRequired = await checkPhoneIsRequired(token);
    if (isPhoneRequired?.isRequired) {
      setPhoneRequired(true);
    }
  };

  const handleValidatePhone: HandleValidatePhone = async (phoneNumber) => {
    if (!isPhoneRequired) {
      return true;
    }

    const checkPhone = await validatePhoneNumber(phoneNumber!);

    if (!checkPhone?.isValid) {
      setError(DOB_VALIDATION_ERROR.INCORRECT_MOBILE);
      return false;
    }

    return true;
  };

  const handleValidateDateOfBirth: HandleValidateDateOfBirth = async ({
    dateOfBirth,
    phone,
  }) => {
    if (!dateOfBirth) {
      return false;
    }

    try {
      const userIdentity = await verifySampleDrawWithoutLogging(
        token,
        dateOfBirth,
        phone
      );

      if (!userIdentity?.profileInfo.isValid) {
        setError(DOB_VALIDATION_ERROR.INCORRECT_DOB);

        attemptsLeftRef.current--;
        if (attemptsLeftRef.current < 1) {
          setBloodDrawScreen(
            ScheduleBloodDrawScreens.WELCOME_DOB_VERIFY_FAILED_SCREEN
          );
        }
        return false;
      }

      const testCardStatus = userIdentity.sampleInfo.sample?.testCardStatus;
      if (
        userIdentity.sampleInfo.isDrawRequested &&
        (testCardStatus === TestCardStatus.APPT_REQUESTED ||
          testCardStatus === TestCardStatus.APPT_CONFIRMED)
      ) {
        setBloodDrawScreen(ScheduleBloodDrawScreens.VIEW_BLOOD_DRAW_DETAILS);
        return false;
      }

      return true;
    } catch (error) {
      setBloodDrawScreen(
        ScheduleBloodDrawScreens.SCHEDULE_BLOOD_DRAW_FAILED_SCREEN
      );
      return false;
    }
  };

  const handleSubmit = async ({
    dateOfBirth,
    phoneNumber,
  }: {
    dateOfBirth: string;
    phoneNumber?: string;
  }) => {
    const isPhoneValidated = await handleValidatePhone(phoneNumber);
    if (!isPhoneValidated) {
      return;
    }

    const isDateOfBirthValidated = await handleValidateDateOfBirth({
      dateOfBirth,
      phone: phoneNumber,
    });
    if (!isDateOfBirthValidated) {
      return;
    }

    openAcknowdledgementsDialog();
  };

  const openAcknowdledgementsDialog = () => {
    acknowdledgementsDialog.open({
      userEmail: "",
      onAgreeAcknowledgements: () => {
        heap.track(HEAP_EVENTS.upp_mp_guestflow, {
          business_unit: drawRequestDataForHeapEventData?.business_unit,
          guest_choice: GUEST_CHOICE.guest_flow,
          test_name: drawRequestDataForHeapEventData?.test_name,
          lims_clinic_id: drawRequestDataForHeapEventData?.lims_clinic_id,
          draw_coordinator: drawRequestDataForHeapEventData?.draw_coordinator,
        });
        setBloodDrawScreen(ScheduleBloodDrawScreens.PERSONAL_INFO_SCREEN);
      },
      onCloseDialog: acknowdledgementsDialog.close,
      flow: ACKNOWLEDGMENT_FLOW.GUEST,
    });
  };

  React.useEffect(() => {
    handleCheckPhoneIsRequired(token);
  }, []);

  React.useEffect(() => {
    if (token) {
      getTokenLanguage(token).then((lang) => setUserLanguage(lang));
    }
  }, [token]);

  return (
    <div className="welcome-dob-screen">
      {acknowdledgementsDialog.getDialog()}
      <DateOfBirthVerificationScreen
        authTitle={
          <AuthTitle
            title={intl.formatMessage(messages.welcomeBloodDrawDobTitle)}
            subtitle={intl.formatMessage(messages.welcomeBloodDrawDobSubTitle)}
          />
        }
        isPhoneRequired={isPhoneRequired}
        onSubmitForm={handleSubmit}
        loadingForm={isLoading}
        heapEventName={HEAP_EVENTS.upp_click_contactnatera}
        heapEventProps={{
          location: HeapEventLocation.mp_flow_initiation_page,
        }}
      />
    </div>
  );
};

export default WelcomeDobScreen;
